import styled, { keyframes } from 'styled-components';

const variantMapper = (theme: any) => ({
  primary: {
    backgroundColor: theme.primary.primary,
    color: theme.text.primary,
    border: 'unset',
    disabledBorder: 'unset',
    hoverBorder: 'unset',
    activeBorder: 'unset',
    disabledBackgroundColor: theme.primary.lighter,
    disabledColor: theme.text.buttonDisabled,
    hoverBackgroundColor: theme.primary.light,
    hoverColor: theme.text.primary,
    activeBackgroundColor: theme.primary.dark,
    activeColor: theme.text.primary,
  },
  white: {
    backgroundColor: theme.background.primary,
    color: theme.primary.primary,
    border: 'unset',
    disabledBorder: 'unset',
    hoverBorder: 'unset',
    activeBorder: 'unset',
    disabledBackgroundColor: 'rgba(255, 255, 255, 0.5)',
    disabledColor: theme.primary.primary,
    hoverBackgroundColor: 'rgba(255, 255, 255, 0.9)',
    hoverColor: theme.primary.primary,
    activeBackgroundColor: 'rgba(255, 255, 255, 0.7)',
    activeColor: theme.primary.primary,
  },
  secondary: {
    backgroundColor: theme.background.primary,
    color: theme.text.title,
    border: `1px solid ${theme.border.primary}`,
    disabledBorder: `1px solid ${theme.border.secondary}`,
    hoverBorder: `1px solid ${theme.primary.lightest}`,
    activeBorder: `1px solid ${theme.primary.primary}`,
    disabledBackgroundColor: theme.background.primary,
    disabledColor: theme.text.disabled,
    hoverBackgroundColor: theme.primary.background,
    hoverColor: theme.primary.primary,
    activeBackgroundColor: theme.primary.background,
    activeColor: theme.primary.primary,
  },
  secondaryLight: {
    backgroundColor: theme.primary.background,
    color: theme.primary.primary,
    fontWeight: 600,
    border: `1px solid ${theme.primary.lighter}`,
    disabledBorder: `1px solid ${theme.border.secondary}`,
    hoverBorder: `1px solid ${theme.primary.lightest}`,
    activeBorder: `1px solid ${theme.primary.primary}`,
    disabledBackgroundColor: theme.background.primary,
    disabledColor: theme.text.disabled,
  },
  link: {
    backgroundColor: 'transparent',
    color: theme.text.regular,
    border: 'none',
    disabledBorder: 'none',
    hoverBorder: 'none',
    activeBorder: 'none',
    disabledBackgroundColor: 'transparent',
    disabledColor: theme.text.disabled,
    hoverBackgroundColor: 'transparent',
    hoverColor: theme.primary.primary,
    activeBackgroundColor: 'transparent',
    activeColor: theme.primary.dark,
  },
  danger: {
    backgroundColor: theme.status.error.primary,
    color: theme.text.primary,
    border: 'none',
    disabledBorder: 'none',
    hoverBorder: 'none',
    activeBorder: 'none',
    disabledBackgroundColor: theme.status.error.background,
    disabledColor: theme.text.primary,
    hoverBackgroundColor: theme.status.error.background,
    hoverColor: theme.text.primary,
    activeBackgroundColor: theme.status.error.primary,
    activeColor: theme.text.primary,
  },
});

export const StyledButton = styled.button<{
  variant: string;
  fullWidth: boolean;
  disabled: boolean;
  $isBouncy?: boolean;
}>`
  position: relative;
  overflow: hidden; /* Keep ripple within bounds */
  background-color: ${({ variant, theme }) =>
    variantMapper(theme)[variant].backgroundColor};
  flex-shrink: 0;
  color: ${({ variant, theme }) => variantMapper(theme)[variant].color};
  border: ${({ variant, theme }) => variantMapper(theme)[variant].border};
  padding-left: 16px;
  padding-right: 16px;
  height: 48px;
  border-radius: 999px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: normal;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  /* Animation readiness - only for non-bouncy buttons */
  ${({ $isBouncy }) =>
    !$isBouncy &&
    `
    transition: transform 90ms cubic-bezier(0.33, 0, 0.67, 1);
    will-change: transform;
  `}

  /* Remove shadow completely */
  box-shadow: none !important;


  ${({ fullWidth }) =>
    fullWidth
      ? `
    width: 100%;
  `
      : `
    width: fit-content;
  `}
  ${({ disabled, variant, theme }) =>
    disabled
      ? `
    background-color: ${variantMapper(theme)[variant].disabledBackgroundColor};
    color: ${variantMapper(theme)[variant].disabledColor};
    border: ${variantMapper(theme)[variant].disabledBorder};
    cursor: not-allowed;
  `
      : `
    @media (hover: hover) {
      &:hover {
        background-color: ${variantMapper(theme)[variant].hoverBackgroundColor};
        color: ${variantMapper(theme)[variant].hoverColor};
        border: ${variantMapper(theme)[variant].hoverBorder};
      }
    }
    &:active {
      background-color: ${variantMapper(theme)[variant].activeBackgroundColor};
      color: ${variantMapper(theme)[variant].activeColor};
      border: ${variantMapper(theme)[variant].activeBorder};
      /* Only apply transform/shadow animation for bouncy buttons */
      ${({ $isBouncy }) =>
        !$isBouncy &&
        `
        transform: none;
        box-shadow: none;
      `}
    }
  `}

  /* Respect reduced motion preference */
  @media (prefers-reduced-motion: reduce) {
    transform: none !important;
    transition: none !important;
    animation: none !important;
    box-shadow: none !important;

    &::before {
      display: none !important;
    }
  }
`;

/* Ripple effect container */
const rippleExpand = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(0.2);
    opacity: 0.32;
  }
  70% {
    transform: translate(-50%, -50%) scale(1.4);
    opacity: 0.18;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.6);
    opacity: 0;
  }
`;

export const RippleContainer = styled.span<{
  $x: number;
  $y: number;
  $variant: string;
}>`
  position: absolute;
  left: ${({ $x }) => $x}px;
  top: ${({ $y }) => $y}px;
  width: 200%;
  height: 200%;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(180, 215, 255, 0.32) 0%,
    rgba(180, 215, 255, 0) 100%
  );
  transform: translate(-50%, -50%) scale(0.2);
  pointer-events: none;
  z-index: 0;
  animation: ${rippleExpand} 140ms ease-out forwards;
`;
